/** Jelly economy facade over the REST API, with a localStorage offline cache.
 *
 * Server contract note: POST /scores increments the progress jelly_balance
 * server-side. So after submitting a score we RE-FETCH progress and overwrite
 * the local balance with the authoritative value — never add twice. */
import * as api from './api-client'
import { getPlayerId, setPlayerId } from './player-identity'
import { Emitter } from '../utils/events'
import { CosmeticSlot, EquippedCosmetics, EMPTY_EQUIP, COSMETIC_SLOTS, slotOf } from '../data/cosmetics'

const CACHE_KEY = 'bluebob_econ'
const NICK_KEY = 'bluebob_nick'

export interface EconomyState {
  playerId: string | null
  nickname: string
  jelly: number
  unlocked: string[]
  /** Which owned cosmetic is active per slot (local display preference). */
  equipped: EquippedCosmetics
  online: boolean
}

type Events = { change: EconomyState }

function loadNick(): string {
  try {
    return localStorage.getItem(NICK_KEY) || ''
  } catch {
    return ''
  }
}
function saveNick(n: string): void {
  try {
    localStorage.setItem(NICK_KEY, n)
  } catch {
    /* ignore */
  }
}

/** A finished run whose server submission hasn't been confirmed — replayed on the
 * next online init so offline-earned jelly is never lost. `runId` is sent as
 * `client_run_id` so a future server-side de-dupe can credit each run exactly once. */
interface PendingRun {
  runId: string
  score: number
  level: number
  jelly_earned: number
  nickname: string
  attempts: number
}
const PENDING_KEY = 'bluebob_pending'
const MAX_REPLAY_ATTEMPTS = 5 // drop a run that keeps failing (e.g. a permanent validation error)

function newRunId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `r-${Date.now()}-${Math.floor(Math.random() * 1e9)}`
  }
}
function loadPending(): PendingRun[] {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]')
  } catch {
    return []
  }
}
function savePending(q: PendingRun[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(q))
  } catch {
    /* ignore */
  }
}
function queuePending(run: PendingRun): void {
  const q = loadPending()
  q.push(run)
  savePending(q)
}

class Economy {
  private bus = new Emitter<Events>()
  state: EconomyState = {
    playerId: getPlayerId(),
    nickname: loadNick(),
    jelly: 0,
    unlocked: [],
    equipped: { ...EMPTY_EQUIP },
    online: false,
  }

  constructor() {
    this.loadCache()
  }

  onChange(fn: (s: EconomyState) => void): () => void {
    return this.bus.on('change', fn)
  }

  private emit(): void {
    this.bus.emit('change', this.state)
  }

  private loadCache(): void {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (raw) {
        const c = JSON.parse(raw)
        this.state.jelly = c.jelly ?? 0
        this.state.unlocked = c.unlocked ?? []
        this.state.equipped = { ...EMPTY_EQUIP, ...(c.equipped ?? {}) }
      }
    } catch {
      /* ignore */
    }
    this.reconcileEquipped()
  }

  private saveCache(): void {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          jelly: this.state.jelly,
          unlocked: this.state.unlocked,
          equipped: this.state.equipped,
        }),
      )
    } catch {
      /* ignore */
    }
  }

  /** Drop any equipped id the player no longer owns (e.g. after a server progress reset),
   * so the renderer never tries to wear an un-owned cosmetic. */
  private reconcileEquipped(): void {
    for (const slot of COSMETIC_SLOTS) {
      const id = this.state.equipped[slot]
      if (id && !this.state.unlocked.includes(id)) this.state.equipped[slot] = null
    }
  }

  get jelly(): number {
    return this.state.jelly
  }

  isUnlocked(id: string): boolean {
    return this.state.unlocked.includes(id)
  }

  getEquipped(): EquippedCosmetics {
    return this.state.equipped
  }

  /** True when this owned cosmetic is the active one in its slot. */
  isEquipped(id: string): boolean {
    const slot = slotOf(id)
    return slot !== null && this.state.equipped[slot] === id
  }

  /** Equip a cosmetic (or pass null to clear the slot). Ignores ids the player doesn't own
   * or that don't belong to the slot. Persisted locally + emits a change. */
  setEquipped(slot: CosmeticSlot, itemId: string | null): void {
    if (itemId !== null && (!this.state.unlocked.includes(itemId) || slotOf(itemId) !== slot)) return
    if (this.state.equipped[slot] === itemId) return
    this.state.equipped[slot] = itemId
    this.saveCache()
    this.emit()
  }

  setNickname(n: string): void {
    this.state.nickname = n.slice(0, 20)
    saveNick(this.state.nickname)
    this.emit()
  }

  /** Ensure a player exists and load authoritative progress. Falls back to cache offline. */
  async init(): Promise<EconomyState> {
    let createdThisCall = false
    try {
      if (!this.state.playerId) {
        const p = await api.createPlayer(this.state.nickname)
        this.state.playerId = p.id
        setPlayerId(p.id)
        createdThisCall = true
      }
      await this.flushPending() // replay any offline-queued runs before trusting server progress
      const prog = await api.getProgress(this.state.playerId!)
      this.state.jelly = prog.jelly_balance
      this.state.unlocked = prog.unlocked_items
      this.state.online = true
    } catch (e) {
      const status = (e as { status?: number })?.status
      if (status === 404 && !createdThisCall) {
        // Player genuinely gone (e.g. server DB reset) — mint a fresh one once.
        try {
          const p = await api.createPlayer(this.state.nickname)
          this.state.playerId = p.id
          setPlayerId(p.id)
          await this.flushPending()
          const prog = await api.getProgress(p.id)
          this.state.jelly = prog.jelly_balance
          this.state.unlocked = prog.unlocked_items
          this.state.online = true
        } catch {
          this.state.online = false
        }
      } else {
        // Transient (network / 5xx) — keep the existing id + cached balance, retry next init.
        // Do NOT mint a new player, which would orphan the old id's server-side progress.
        this.state.online = false
      }
    }
    this.reconcileEquipped() // drop equipped cosmetics no longer owned after authoritative load
    this.saveCache()
    this.emit()
    return this.state
  }

  /** Submit a finished run. Optimistic locally, server-authoritative on success.
   * Only a failed *submit* re-queues the run — a failed follow-up progress read
   * leaves the run already credited server-side, so re-submitting it would
   * double-count. Each run carries a stable runId for de-dupe on replay. */
  async commitResult(score: number, level: number, jellyEarned: number): Promise<void> {
    this.state.jelly += jellyEarned // optimistic for instant UI
    this.saveCache()
    this.emit()
    const run: PendingRun = {
      runId: newRunId(),
      score,
      level,
      jelly_earned: jellyEarned,
      nickname: this.state.nickname || 'BlueBob',
      attempts: 0,
    }
    if (!this.state.playerId) {
      queuePending(run)
      return
    }
    let submitted = false
    try {
      await this.flushPending() // drain any offline backlog now we're online (in-session recovery)
      await api.submitScore({
        player_id: this.state.playerId,
        client_run_id: run.runId,
        nickname: run.nickname,
        score,
        level,
        jelly_earned: jellyEarned,
      })
      submitted = true
      const prog = await api.getProgress(this.state.playerId) // already includes jellyEarned
      this.state.jelly = prog.jelly_balance
      this.state.unlocked = prog.unlocked_items
      this.reconcileEquipped() // keep equipped in sync if the server's owned set changed
      this.state.online = true
    } catch {
      if (submitted) {
        // The submit reached the server and credited the run — only the progress
        // refresh failed. Stay online (it proved connectivity); balance refreshes on
        // next init. Re-queueing here would double-credit.
        this.state.online = true
      } else {
        this.state.online = false
        queuePending(run) // submit never reached the server → safe to replay
      }
    }
    this.saveCache()
    this.emit()
  }

  /** Replay queued offline submissions one at a time, persisting the shrunk queue
   * after each accepted run so a crash can't replay an already-accepted run more
   * than once. Drops a run that keeps failing past MAX_REPLAY_ATTEMPTS. */
  private async flushPending(): Promise<void> {
    const playerId = this.state.playerId
    if (!playerId) return
    let queue = loadPending()
    while (queue.length) {
      const run = queue[0]
      try {
        await api.submitScore({
          player_id: playerId,
          client_run_id: run.runId,
          nickname: run.nickname,
          score: run.score,
          level: run.level,
          jelly_earned: run.jelly_earned,
        })
        queue = queue.slice(1) // accepted — drop immediately
      } catch (e) {
        const status = (e as { status?: number })?.status
        const permanent =
          typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429
        if (!permanent) {
          // Transient (offline / timeout / 5xx / rate-limit) — keep the run indefinitely
          // and retry next init. Never discard earned jelly for a degraded server.
          savePending(queue)
          return
        }
        run.attempts++
        if (run.attempts >= MAX_REPLAY_ATTEMPTS) {
          queue = queue.slice(1) // permanent validation error (4xx) — give up
        } else {
          savePending(queue)
          return
        }
      }
      savePending(queue)
    }
  }

  async purchase(itemId: string): Promise<{ new_balance: number; unlocked_items: string[] }> {
    if (!this.state.playerId) throw new Error('You are offline — purchases need the server.')
    const r = await api.purchaseItem(this.state.playerId, itemId)
    this.state.jelly = r.new_balance
    this.state.unlocked = r.unlocked_items
    const slot = slotOf(itemId)
    if (slot) this.state.equipped[slot] = itemId // auto-equip the freshly bought cosmetic
    this.saveCache()
    this.emit()
    return r
  }

  async leaderboard(limit = 20) {
    return (await api.getLeaderboard(limit)).scores
  }

  async shop() {
    return (await api.getShopItems()).items
  }
}

export const economy = new Economy()
